import React, { useState, useEffect } from 'react';
import { saleRecords as initialSales, customers as staticCustomers } from '../../data/appData';
import { fetchSales, fetchCustomers, fetchProducts, saveSale, deleteSale } from '../../services/api';
import './Sale.css';

const now = new Date();
const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

const formatDateForAPI = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-") && dateStr.split("-")[0].length === 4) {
    // YYYY-MM-DD to DD-MM-YYYY
    const [y, m, d] = dateStr.split("-");
    const formattedDate = `${d}-${m}-${y}`;
    console.log(`Formatting UI Date (${dateStr}) to API Date (${formattedDate})`);
    return formattedDate;
  }
  return dateStr;
};

const formatDateForUI = (date) => {
  if (!date) return "";
  let d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  let year = d.getFullYear();
  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;
  return [year, month, day].join('-');
};

const getTodayForUI = () => formatDateForUI(new Date());
const getFirstDayForUI = () => formatDateForUI(firstDayOfMonth);

const today = '01-04-2026'; // Legacy default, moving to dynamic

export default function Sale({ user }) {

  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [fromDate, setFromDate] = useState(getFirstDayForUI());
  const [toDate, setToDate] = useState(getTodayForUI());
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState('');

  const companyId = user?.Companyid || user?.company_id || "1";

  const [form, setForm] = useState({
    bill_no: '',
    date: '',
    customer_id: '',
    product: '',
    qty: '',
    rate: '',
    payment_mode: ''
  });

  useEffect(() => {
    loadData();
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Load Sales with filters
      const salesFilters = {
      Companyid: companyId,
      Name: "",
      Product: "",
      FromDate: fromDate ? formatDateForAPI(fromDate) : "",
      ToDate: toDate ? formatDateForAPI(toDate) : ""
    };
      
      const salesRes = await fetchSales(salesFilters);
      if (salesRes.success) {
        const raw = salesRes.Response || salesRes.data || salesRes.item;
        const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
        setSales(list);
      } else {
        console.warn('Sales fetch returned unsuccessful:', salesRes);
      }

      // 2. Load Customers
      const custRes = await fetchCustomers(companyId);
      if (custRes.success) {
        const raw = custRes.Response || custRes.data || custRes.item;
        const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
        setCustomers(list);
      }

      // 3. Load Products
      const prodRes = await fetchProducts(companyId);
      if (prodRes.success) {
        setProducts(prodRes.Response || prodRes.data || []);
      }

    } catch (err) {
      console.error('Failed to load Sale data:', err);
      setError('Connection failed. Please check your network or try again.');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditId(null);
    setForm({
      bill_no: '',
      date: '',
      customer_id: '',
      product: '',
      qty: '',
      rate: '',
      payment_mode: ''
    });
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditId(s.ID || s.sale_id || s.id || s.IDS);
    setForm({
      bill_no: s.bill_no || s.billNo || '',
      date: s.Date1
        ? `${s.Date1.split('-')[2]}-${s.Date1.split('-')[1]}-${s.Date1.split('-')[0]}`
        : formatDateForUI(new Date()),      
      customer_id: '',
      product: s.Product || s.product || 'Petrol',
      qty: s.Qty || s.qty || '',
      rate: s.Rate || s.rate || '',
      payment_mode: (s.PaymentMode || s.payment_mode || 'CASH').toUpperCase()    });
    // Try to find customer by name to set customer_id
    const customer = customers.find(c => {
      const cName = (c.name || c.Name || '').toLowerCase();
      const sName = (s.Customer || s.Name || s.customer || s.customer_name || '').toLowerCase();
      return cName === sName;
    });
    if (customer) {
      setForm(prev => ({ ...prev, customer_id: customer.Customerid || customer.customer_id || customer.id }));
    }
    setShowForm(true);
  };

  const handleSave = async () => {

  if (!form.date) {
    alert("Date is required");
    return;
  }

  if (!form.qty || !form.rate || form.customer_id === '' || !form.payment_mode || !form.product) {
    alert("Please fill all required fields");
    return;
  }

    const customer = form.customer_id
      ? customers.find(c => (c.customer_id || c.id || c.Customerid) == form.customer_id)
      : null;

    const qty = parseFloat(form.qty);
    const rate = parseFloat(form.rate);
    const amount = qty * rate;

    // Proactively resolve Customerid from the selected customer record
    let resolvedCustomerId = "0";
    if (customer) {
      resolvedCustomerId = customer.Customerid || customer.customer_id || customer.id || "0";
    }

    // Mapping to API TitleCase fields as requested
    const apiData = {
      Companyid: companyId,
      Customerid: resolvedCustomerId || "0",
      Name: customer ? (customer.name || customer.Name) : 'Cash',
      Product: form.product,
      Qty: qty.toString(),
      Rate: rate.toString(),
      Amount: amount.toFixed(2),
      Date1: formatDateForAPI(form.date),
      PaymentMode: form.payment_mode,
      IDS: editId ? editId.toString() : "0"
    };

    setLoading(true);
    try {
      const res = await saveSale(apiData);
      if (res.success || res.Response === "UPDATED" || res.Response === "INSERTED") {
        alert(res.message || `Sale ${editId ? 'updated' : 'saved'} successfully!`);
        await loadData();        
        setShowForm(false);
        setEditId(null);
        setForm({
          bill_no: '',
          date: '',
          customer_id: '',
          product: '',
          qty: '',
          rate: '',
          payment_mode: ''
        });
      } else {
        setError(res.message || 'Failed to save sale');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Error connecting to API');
    } finally {
      setLoading(false);
    }

  };

  const handleDelete = async (s) => {
    const id = s.ID || s.sale_id || s.id || s.IDS;
    if (!id) {
      alert("Cannot delete: Record ID not found");
      return;
    }
    if (!window.confirm('Are you sure you want to delete this sale?')) return;

    setLoading(true);
    try {
      const res = await deleteSale(id);
      if (res.success || res.Response === "DELETED" || res.status === "Success") {
        alert(res.message || "Sale deleted successfully");
        await loadData();
      } else {
        alert(res.message || "Failed to delete sale");
      }
    } catch (err) {
      console.error("Delete sale error:", err);
      alert("Error connecting to API");
    } finally {
      setLoading(false);
    }
  };


  const filtered = sales.filter(s =>
    (s.bill_no || s.billNo || '').toLowerCase().includes(filter.toLowerCase()) ||
    (s.customer_name || s.customer || s.customer || s.Name || s.Customer || '').toLowerCase().includes(filter.toLowerCase())
  );

  const total = filtered.reduce((s, r) => s + (parseFloat(r.amount || r.Amount) || 0), 0);

  return (

    <div className="sale-page">

      <div className="page-header">
        <h2 className="page-title">Sales / Transactions</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="date-filter" style={{ display: 'flex', gap: 5, alignItems: 'center', background: '#fff', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>
            <input 
              type="date" 
              className="form-control" 
              style={{ width: 130, border: 'none', padding: 0, fontSize: 13 }}
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
            />
            <span>to</span>
            <input 
              type="date" 
              className="form-control" 
              style={{ width: 130, border: 'none', padding: 0, fontSize: 13 }}
              value={toDate}
              onChange={e => setToDate(e.target.value)}
            />
            <button className="btn btn-secondary btn-sm" onClick={loadData} title="Apply Filter">
              <i className="bi bi-filter"></i>
            </button>
          </div>
          <button className="btn btn-primary btn-sm" onClick={openAdd}>
            ＋ New Sale
          </button>
        </div>
      </div>

      {showForm && (

        <div className="section-card">

          <div className="section-header">{editId ? 'Edit Sale Entry' : 'New Sale Entry'}</div>

          <div className="form-row">

            {/* <div className="form-group">
              <label>Bill No.</label>
              <input
                className="form-control"
                value={form.bill_no}
                onChange={e => setForm({ ...form, bill_no: e.target.value })}
                placeholder="SL-2420"
              />
            </div> */}

            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                className="form-control"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
              />
            </div>

            <div className="form-group">

              <label>Customer</label>

              <select
                className="form-control"
                value={form.customer_id}
                disabled={!!editId}
                onChange={e => setForm({
                  ...form,
                  customer_id: e.target.value,
                  payment_mode: e.target.value ? 'Cash' : ''
                })}
              >
                <option value="">Select Customer</option>
                {customers.map(c => {
                  const id = c.customer_id || c.id || c.Customerid;
                  const name = c.name || c.Name;
                  return (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  );
                })}
              </select>

            </div>

            <div className="form-group">
              <label>Product</label>
              <select
                className="form-control"
                value={form.product}
                onChange={e => setForm({ ...form, product: e.target.value })}
              >
                <option value="">Select Product</option>
                {products.map(p => (
                  <option key={p.Productid || p.id} value={p.Name}>
                    {p.Name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Quantity (L)</label>
              <input
                type="number"
                className="form-control"
                value={form.qty}
                onChange={e => setForm({ ...form, qty: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Rate (₹)</label>
              <input
                type="number"
                className="form-control"
                value={form.rate}
                onChange={e => setForm({ ...form, rate: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Payment Mode</label>
              <select
                className="form-control"
                value={form.payment_mode}
                onChange={e => setForm({ ...form, payment_mode: e.target.value })}
              >
                <option value="">Select Payment Mode</option>
                <option value="CASH">Cash</option>
                <option value="UPI">UPI</option>
                <option value="CARD">Card</option>
                <option value="CREDIT">Credit</option>
              </select>
            </div>

            {form.qty && form.rate && (

              <div className="form-group">

                <label>Amount</label>

                <div
                  className="balance-value"
                  style={{
                    color: 'var(--secondary)',
                    background: '#f0fff4',
                    borderColor: '#9ae6b4',
                    padding: '8px 12px',
                    border: '1px solid',
                    borderRadius: 6
                  }}
                >

                  ₹{(parseFloat(form.qty) * parseFloat(form.rate)).toFixed(2)}

                </div>

              </div>

            )}

          </div>

          <div className="action-bar" style={{ padding: '0 16px 14px' }}>

            <button className="btn btn-primary" onClick={handleSave}>
              {editId ? "Update" : "Save "}
            </button>

            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>
              ✕ Cancel
            </button>

          </div>

        </div>

      )}

      <div className="section-card">

        <div className="section-header">
          Sales Records — {filtered.length} records | Total: ₹{total.toLocaleString()}
        </div>

        <div className="table-wrapper">

          <table className="data-table">

            <thead>
              <tr>
                {/* <th className="text-left">Bill No.</th> */}
                <th className="text-center">Date</th>
                <th className="text-left">Customer</th>
                <th className="text-left">Product</th>
                <th className="text-right">Qty (L)</th>
                <th className="text-right">Rate (₹)</th>
                <th className="text-right">Amount (₹)</th>
                <th className="text-center">PAYMENT MODE</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>

              {filtered.map((r, i) => (
              <tr key={r.ID || r.IDS || i}>                  
              {/* <td className="text-left" style={{ fontFamily: 'monospace', color: 'var(--primary)', fontWeight: 700 }}>
                    {r.bill_no || r.billNo || '—'}
                  </td> */}
                  <td className="text-center">{r.Date1 || r.date || '—'}</td>
                  <td className="text-left" style={{ fontWeight: 600 }}>
                    {r.Customer || r.customer || r.Name || r.name || r.customer_name || 'Cash'}
                  </td>
                  <td className="text-left">
                    <span className="badge badge-info">{r.Product || '—'}</span>
                  </td>
                  <td className="text-right">
                    {parseFloat(r.Qty || r.qty || 0).toFixed(3)}L
                  </td>
                  <td className="text-right">
                    ₹{parseFloat(r.Rate || r.rate || 0).toFixed(2)}
                  </td>
                  <td className="text-right" style={{ fontWeight: 700 }}>
                    ₹{parseFloat(r.Amount || r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="text-center">
                    <span className={`badge ${['CASH', 'UPI', 'CARD', 'PAID', 'ONLINE'].some(m => (r.PaymentMode || r.payment_mode || r.Payment_Mode || r.Mode || r.status || '').toUpperCase().includes(m)) ? 'badge-success' : 'badge-warning'}`}>
                        {(r.PaymentMode || r.payment_mode || r.Payment_Mode || r.Mode || '').toUpperCase() || 'CREDIT'}                    
                  </span>
                  </td>
                  <td className="text-center">
                    <button className="icon-btn" onClick={() => openEdit(r)} title="Edit">
                      <i className="bi bi-pencil-square"></i>
                    </button>
                    </td>
                </tr>
              ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>

  );
}